import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Header from './Header';
import Footer from './Footer';
import '../styles.css';

const LeadBoard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Pagination state
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;

  // Pagination logic
  const totalPages = useMemo(() => Math.ceil(leaderboard.length / itemsPerPage), [leaderboard.length, itemsPerPage]);
  const paginatedLeaderboard = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return leaderboard.slice(startIndex, endIndex);
  }, [leaderboard, currentPage, itemsPerPage]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to view the leaderboard.');
        navigate('/login');
        return;
      }

      try {
        console.log('Fetching leaderboard from:', `${API_URL}/api/referrals/leaderboard`); // Debug log
        console.log('Using token:', token); // Debug log
        const response = await axios.get(`${API_URL}/api/referrals/leaderboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Leaderboard response status:', response.status); // Debug log
        console.log('Leaderboard response data:', response.data); // Debug log

        // The API returns a sorted list of { referrer_id, total_rewards }
        // Map the data to include ranks
        const rankedLeaderboard = response.data.map((entry, index) => ({
          rank: index + 1,
          user_id: entry.referrer_id,
          referral_rewards: parseFloat(entry.total_rewards) || 0,
        }));

        setLeaderboard(rankedLeaderboard);
      } catch (err) {
        console.error('Error fetching leaderboard:', err.response ? err.response.data : err.message);
        console.error('Error status:', err.response?.status); // Debug log
        setError(err.response?.status === 404
          ? 'Leaderboard endpoint not found. Please check the backend setup.'
          : err.response?.status === 401
          ? 'Unauthorized access. Please log in again.'
          : 'Failed to load leaderboard data.');
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [navigate, API_URL]);

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <div className="row">
          <div className="card p-4 mb-4">
            <h2 className="text-center mb-4">Referral Reward Leaderboard</h2>
            {error && <p className="text-danger text-center">{error}</p>}
            {loading ? (
              <p className="text-center">Loading leaderboard...</p>
            ) : leaderboard.length > 0 ? (
              <>
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>User ID</th>
                      <th>Referral Rewards (ABAY)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLeaderboard.map((user) => (
                      <tr key={user.user_id}>
                        <td>{user.rank}</td>
                        <td>{user.user_id}</td>
                        <td>{user.referral_rewards.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 mt-3">
                  <FormControl variant="outlined" size="small" sx={{ minWidth: '100px' }}>
                    <InputLabel sx={{ color: '#000' }}>Page</InputLabel>
                    <Select
                      value={currentPage}
                      onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
                      label="Page"
                      sx={{
                        color: '#000',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '& .MuiSvgIcon-root': { color: '#000' },
                      }}
                    >
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <MenuItem key={page} value={page} sx={{ color: '#000' }}>{page}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <span style={{ color: '#000', alignSelf: 'center' }}>
                    of {totalPages}
                  </span>
                  <FormControl variant="outlined" size="small" sx={{ minWidth: '120px' }}>
                    <InputLabel sx={{ color: '#000' }}>Items per Page</InputLabel>
                    <Select
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                      label="Items per Page"
                      sx={{
                        color: '#000',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
                        '& .MuiSvgIcon-root': { color: '#000' },
                      }}
                    >
                      <MenuItem value={10} sx={{ color: '#000' }}>10</MenuItem>
                      <MenuItem value={25} sx={{ color: '#000' }}>25</MenuItem>
                      <MenuItem value={50} sx={{ color: '#000' }}>50</MenuItem>
                      <MenuItem value={100} sx={{ color: '#000' }}>100</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </>
            ) : (
              <p className="text-center">No users found on the leaderboard.</p>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default LeadBoard;
